import { useCallback, useEffect, useState } from "react";

/**
 * The site's light or dark, chosen with the header's sun/moon (owner,
 * 2026-10-22). Until a reader picks one the page follows the system's scheme;
 * once picked it is kept on the device, like the reader settings, because it
 * describes a screen and a room rather than a person.
 *
 * It is carried by the same `data-theme` attribute the reader's themes use, so
 * every token and the `dark:` variant already follow it. The reader's own
 * theme wins while a chapter is open; its "Auto" means this choice, and
 * leaving the chapter puts this choice back (readerSettings.ts).
 *
 * index.html applies the stored value before the bundle runs, so a reader who
 * chose dark is not shown a light page first.
 */
export type SiteScheme = "light" | "dark";

export const SCHEME_KEY = "sageleaf.scheme";

export function storedScheme(): SiteScheme | null {
  try {
    const value = localStorage.getItem(SCHEME_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

function systemScheme(): SiteScheme {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Puts the site's choice on the document root, or clears it to follow the system. */
export function applySiteScheme(root: HTMLElement = document.documentElement) {
  const scheme = storedScheme();
  if (scheme) root.dataset.theme = scheme;
  else delete root.dataset.theme;
}

/** The scheme the page is drawn in now, and a switch to the other one. */
export function useSiteScheme(): [SiteScheme, () => void] {
  const [scheme, setScheme] = useState<SiteScheme>(() => storedScheme() ?? systemScheme());

  // Follow the system while nothing is chosen, so a phone that turns dark at
  // sunset shows the right icon.
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const media = matchMedia("(prefers-color-scheme: dark)");
    const follow = () => {
      if (!storedScheme()) setScheme(media.matches ? "dark" : "light");
    };
    media.addEventListener("change", follow);
    return () => media.removeEventListener("change", follow);
  }, []);

  const toggle = useCallback(() => {
    setScheme((current) => {
      const next: SiteScheme = current === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(SCHEME_KEY, next);
      } catch {
        // Private browsing or full storage: the choice lasts this page.
        document.documentElement.dataset.theme = next;
        return next;
      }
      applySiteScheme();
      return next;
    });
  }, []);

  return [scheme, toggle];
}
